<template>
    <div class="match-info IN_MatchCard_Btn_MatchInfo" v-on:click="goToMatchDetail()">
        <div class="match-info-team">
            <div
                class="match-info-team-name JARVIS_MatchCard_Img_TeamNameA"
                v-bind:class="{
                    _favorite: info.isHomeFavorite,
                    _underdog: !info.isHomeFavorite,
                }"
            >
                <span>{{ homeTeamName }}</span>
                <div
                    v-if="info.isLive && displayLiveScoreData"
                    v-bind:class="{ runningTeam: isRunningTeam('home') && info != null }"
                    class="match-info-team-score"
                >
                    {{ displayLiveScoreData.homeScore }}
                </div>
            </div>
            <div
                class="match-info-team-name JARVIS_MatchCard_Img_TeamNameB"
                v-bind:class="{
                    _favorite: info.isAwayFavorite,
                    _underdog: !info.isAwayFavorite,
                }"
            >
                <span>{{ awayTeamName }}</span>
                <div
                    v-if="info.isLive && displayLiveScoreData"
                    v-bind:class="{ runningTeam: isRunningTeam('away') && info != null }"
                    class="match-info-team-score"
                >
                    {{ displayLiveScoreData.awayScore }}
                </div>
            </div>
        </div>
        <div class="match-info-function">
            <div class="match-info-function-time">
                <div v-if="liveStatus.onLive && displayLiveScoreData" class="match-info-score">
                    <span>
                        {{ displayLiveScoreData.liveScoreInfoDetail }}
                    </span>
                </div>
                <div v-else-if="info.showTime">
                    {{ info.eventDate }} {{ formatShowTime(info.showTime, info.showTimeType) }}
                </div>
            </div>
            <div
                v-if="liveStatus.isToday"
                v-bind:class="['match-info-function-liveTag', { _selectedChannels: liveStatus.tvLive }]"
            >
                {{ getTranslationBySite('live').toUpperCase() }}
            </div>

            <SvgIcon
                v-bind:iconName="isStatisticsBtnShow ? 'sportIcon_statistics' : 'sportIcon_statistics_disabled'"
                class="match-info-function-item"
            />

            <SvgIcon
                v-bind:iconName="isLiveStreamBtnShow ? 'sportIcon_liveTv' : 'sportIcon_liveTv_disabled'"
                class="match-info-function-item"
            />

            <SvgIcon
                v-bind:iconName="isLiveCourtBtnShow ? 'sportIcon_liveCourt' : 'sportIcon_liveCourt_disabled'"
                class="match-info-function-item"
            />

            <SvgIcon
                v-if="!info.isLive && !isOutright"
                iconName="systemIcon_calendar"
                class="match-info-function-item"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref, toRefs, watch } from 'vue';
    import { useLiveStream } from '@/components/liveStream/useLiveStream';
    import useMatchInfoScore from '@/components/match/matchInfo/useMatchInfoScore';
    import { useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import type { IMatch } from '@/interface/IMatch';
    import { MarketPage } from '@/interface/enum';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'CricketMatchInfoInMainPage',
        components: {
            SvgIcon,
        },
        props: {
            info: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            marketPageType: {
                type: String as PropType<MarketPage>,
                default: MarketPage.Unknown,
            },
            matchName: {
                type: String,
                default: ' vs ',
            },
        },
        setup(props) {
            const { info } = toRefs(props);

            const homeTeamName = computed(() => getTranslated(info.value.homeTeam?.teamName) || props.matchName.split(' vs ')[0]);

            const awayTeamName = computed(() => getTranslated(info.value.awayTeam?.teamName) || props.matchName.split(' vs ')[1]);

            const { goToMatchDetail } = useMatchFunction(info);

            const liveStatus = computed(() => ({
                tvLive: info.value.isTvLive,
                sboLive: info.value.isSboLive,
                onLive: info.value.isLive,
                isToday: props.marketPageType === MarketPage.Today,
            }));

            const activeTeam = ref('');
            function isRunningTeam(team: 'home' | 'away') {
                return activeTeam.value === team;
            }

            const {
                displayLiveScoreData,
                liveScoreInfo,
            } = useMatchInfoScore(info);

            watch(liveScoreInfo, (newVal, oldVal) => {
                if (newVal.cricket === null || oldVal.cricket === null) {
                    return;
                }

                if (newVal.cricket.homeScore > oldVal.cricket.homeScore) {
                    activeTeam.value = 'home';
                }

                if (newVal.cricket.homeDismissals > oldVal.cricket.homeDismissals) {
                    activeTeam.value = 'home';
                }

                if (newVal.cricket.awayScore > oldVal.cricket.awayScore) {
                    activeTeam.value = 'away';
                }

                if (newVal.cricket.awayDismissals > oldVal.cricket.awayDismissals) {
                    activeTeam.value = 'away';
                }
            });

            const { marketPage } = storeToRefs(useRootStore());
            const { isStatisticsEnabled } = storeToRefs(useFeatureToggleStore());
            const isOutright = computed(() => marketPage.value === MarketPage.Outright);

            const isStatisticsBtnShow = computed(() => isStatisticsEnabled.value && info.value.hasStatistics);

            const isLiveCourtBtnShow = computed(() => info.value.liveCourtMatchId > 0);

            const { isLiveStreamPlayable } = useLiveStream(info);
            const isLiveStreamBtnShow = computed(() => isLiveStreamPlayable.value);

            return {
                homeTeamName,
                awayTeamName,
                goToMatchDetail,
                isRunningTeam,
                displayLiveScoreData,
                liveStatus,
                formatShowTime,
                getTranslationBySite,
                isOutright,
                isStatisticsBtnShow,
                isLiveCourtBtnShow,
                isLiveStreamBtnShow,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/indiaSite/components/match/matchInfo";

._live .match-info {
    &-team-score {
        color: $primary-color-1;

        &.runningTeam {
            color: #ff760f;
        }
    }
}
</style>
